import { useEffect, useRef, useState } from 'react';
import { ExternalLink, Star, Trash2 } from 'lucide-react';

import { cn } from '@/marketing/lib/utils';
import { Input } from '@/marketing/components/ui/input';
import { formatDate, formatNumber } from '@/marketing/lib/formatters';
import type { ContentTrackingVideo, ContentTrackingVideoSource } from '@/marketing/models/contentTracking';

interface ContentTrackingVideoCellProps {
  video: ContentTrackingVideo;
  canEdit: boolean;
  onDelete: (url: string) => void;
  onSaveSparkAdsCode: (url: string, sparkAdsCode: string) => void;
  onToggleTop: (url: string) => void;
  onSaveMetrics: (url: string, metrics: { views?: number; likes?: number; comments?: number; saves?: number }) => Promise<unknown>;
}

const SOURCE_LABELS: Record<ContentTrackingVideoSource, { label: string; className: string }> = {
  submitted: { label: 'Added by Creator', className: 'tw-bg-blue-50 tw-text-blue-700' },
  administrator: { label: 'Filled by Hub', className: 'tw-bg-amber-50 tw-text-amber-700' },
  legacy: { label: 'Imported', className: 'tw-bg-muted tw-text-muted-foreground' },
};

function shortUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.hostname.replace(/^www\./, '')}${parsed.pathname}`.replace(/\/$/, '');
  } catch {
    return url;
  }
}

function metricOrDash(value: number | null): string {
  return value === null ? '—' : formatNumber(value);
}

const METRIC_KEYS = ['views', 'likes', 'comments', 'saves'] as const;
type MetricKey = (typeof METRIC_KEYS)[number];

function MetricsRow({
  video,
  canEdit,
  onSave,
}: {
  video: ContentTrackingVideo;
  canEdit: boolean;
  onSave: ContentTrackingVideoCellProps['onSaveMetrics'];
}) {
  const [editing, setEditing] = useState<MetricKey | null>(null);
  const [draft, setDraft] = useState('');
  const inFlight = useRef(false);
  const commit = async () => {
    if (!editing || !canEdit || inFlight.current) {
      return;
    }
    const trimmed = draft.trim();
    const value = trimmed === '' ? undefined : Number(trimmed);
    if (value !== undefined && (!Number.isInteger(value) || value < 0)) {
      setEditing(null);
      return;
    }
    if (value !== undefined && value !== video[editing]) {
      inFlight.current = true;
      try {
        await onSave(video.url, { [editing]: value });
      } catch {
        // The mutation reports the error; retain the draft for retry with the refreshed version.
        return;
      } finally {
        inFlight.current = false;
      }
    }
    setEditing(null);
  };
  const evidence = video.fieldEvidence.views;
  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-0.5 tw-text-[11px] tw-tabular-nums">
      {METRIC_KEYS.map((key) =>
        editing === key ? (
          <Input
            key={key}
            autoFocus
            value={draft}
            disabled={!canEdit}
            inputMode="numeric"
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => void commit()}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                void commit();
              }
              if (event.key === 'Escape') {
                setEditing(null);
              }
            }}
            aria-label={`${key} for ${video.url}`}
            className="tw-h-6 tw-w-20 tw-px-1 tw-text-[11px]"
          />
        ) : (
          <button
            key={key}
            type="button"
            disabled={!canEdit}
            title={`${key}: ${video.fieldEvidence[key].source ?? 'not collected'}${video.fieldEvidence[key].observedAt ? ` at ${formatDate(video.fieldEvidence[key].observedAt ?? undefined)}` : ''}`}
            onClick={() => {
              setDraft(video[key] === null ? '' : String(video[key]));
              setEditing(key);
            }}
            className={cn('tw-rounded tw-px-1 hover:tw-bg-muted', video[key] === null && 'tw-text-muted-foreground')}
          >
            <span className="tw-text-muted-foreground">{key.charAt(0).toUpperCase()}</span> {metricOrDash(video[key])}
          </button>
        ),
      )}
      <span className="tw-text-muted-foreground">
        ER {video.engagementRate === null ? '—' : `${video.engagementRate.toFixed(1)}%`}
      </span>
      {evidence.source && (
        <span className="tw-text-[10px] tw-text-muted-foreground" title={evidence.observedAt ?? undefined}>
          · {evidence.source === 'connected' ? 'auto' : evidence.source}
        </span>
      )}
    </div>
  );
}

function SparkAdsCodeField({
  video,
  canEdit,
  onSave,
}: {
  video: ContentTrackingVideo;
  canEdit: boolean;
  onSave: (url: string, sparkAdsCode: string) => void;
}) {
  const [value, setValue] = useState(video.sparkAdsCode ?? '');
  useEffect(() => setValue(video.sparkAdsCode ?? ''), [video.sparkAdsCode]);
  const commit = () => {
    if (!canEdit) return;
    const trimmed = value.trim();
    if (trimmed !== (video.sparkAdsCode ?? '')) {
      onSave(video.url, trimmed);
    }
  };
  return (
    <Input
      value={value}
      disabled={!canEdit}
      placeholder="Spark Ads code"
      onChange={(event) => setValue(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          (event.target as HTMLInputElement).blur();
        }
      }}
      aria-label={`Spark Ads code for ${video.url}`}
      className={cn('tw-h-7 tw-font-mono tw-text-[11px]', !video.sparkAdsCode && 'tw-border-dashed')}
    />
  );
}

export function ContentTrackingVideoCell({
  video,
  canEdit,
  onDelete,
  onSaveSparkAdsCode,
  onToggleTop,
  onSaveMetrics,
}: ContentTrackingVideoCellProps) {
  const source = SOURCE_LABELS[video.source];
  return (
    <div className="tw-flex tw-min-w-[260px] tw-flex-col tw-gap-1.5">
      <div className="tw-flex tw-items-start tw-gap-1.5">
        <a
          href={video.url}
          target="_blank"
          rel="noreferrer"
          className="tw-inline-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-1 tw-text-[12px] tw-text-blue-700 hover:tw-underline"
          title={video.url}
        >
          <span className="tw-truncate">{shortUrl(video.url)}</span>
          <ExternalLink className="tw-h-3 tw-w-3 tw-shrink-0" />
        </a>
        <button
          type="button"
          onClick={() => onToggleTop(video.url)}
          disabled={!canEdit}
          aria-label={video.topVideo ? 'Remove from Top 10' : 'Add to Top 10'}
          aria-pressed={video.topVideo}
          className={cn(
            'tw-ml-auto tw-inline-flex tw-h-5 tw-w-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded',
            video.topVideo ? 'tw-text-amber-500' : 'tw-text-muted-foreground hover:tw-text-amber-500',
            !canEdit && 'tw-opacity-40',
          )}
        >
          <Star className="tw-h-3.5 tw-w-3.5" fill={video.topVideo ? 'currentColor' : 'none'} />
        </button>
        <button
          type="button"
          onClick={() => onDelete(video.url)}
          disabled={!canEdit}
          aria-label={`Delete video ${video.url}`}
          title="Delete video"
          className="tw-inline-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded tw-text-muted-foreground hover:tw-text-rose-700 disabled:tw-opacity-40"
        >
          <Trash2 className="tw-h-3.5 tw-w-3.5" />
        </button>
      </div>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1 tw-text-[10px]">
        <span className={cn('tw-rounded-full tw-px-1.5 tw-py-0.5 tw-font-semibold', source.className)}>
          {source.label}
        </span>
        <span className="tw-capitalize tw-text-muted-foreground">{video.platform}</span>
        <span className="tw-rounded tw-bg-muted tw-px-1.5 tw-py-0.5">{video.mandatory ? 'Mandatory' : 'Optional'}</span>
        {video.postedAt && <span className="tw-text-muted-foreground">· posted {formatDate(video.postedAt)}</span>}
      </div>
      <MetricsRow video={video} canEdit={canEdit && video.hasSubmission} onSave={onSaveMetrics} />
      {video.platform === 'tiktok' && <SparkAdsCodeField video={video} canEdit={canEdit && video.hasSubmission} onSave={onSaveSparkAdsCode} />}
    </div>
  );
}
